/**
 * ZoomSetupGuideModal — comprehensive Zoom app setup walkthrough.
 *
 * Explains the two Zoom Marketplace apps we need, exact scopes, domain
 * allowlisting, and publish-vs-development guidance. Opens from the
 * Zoom Licenses admin page.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ExternalLink, Copy, CheckCircle2, AlertTriangle, Server, Video, Info, MapPin, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const S2S_SCOPES = [
  { scope: 'meeting:write:meeting', purpose: 'Create scheduled meetings (Sprint 2)', required: true },
  { scope: 'meeting:write:meeting:admin', purpose: 'Create meetings under any account user (multi-host setup)', required: true },
  { scope: 'meeting:read:meeting', purpose: 'Read meeting details (join_url, password)', required: true },
  { scope: 'meeting:read:meeting:admin', purpose: 'Admin-level meeting reads', required: true },
  { scope: 'meeting:update:meeting', purpose: 'Reschedule / update classes', required: true },
  { scope: 'meeting:update:meeting:admin', purpose: 'Admin-level updates', required: true },
  { scope: 'meeting:delete:meeting', purpose: 'Cancel classes (delete on Zoom side)', required: true },
  { scope: 'meeting:delete:meeting:admin', purpose: 'Admin-level deletes', required: true },
  { scope: 'report:read:admin', purpose: 'Fetch attendance reports for completed classes', required: true },
  { scope: 'user:read:token', purpose: 'Fetch ZAK tokens so hosts can screen-share inside the embedded client', required: true, note: 'Required by Zoom Client View — without this, the host Start Class button cannot mint a ZAK and screen sharing is disabled' },
  { scope: 'user:read:user', purpose: 'Verify default host user exists', required: false, note: 'Nice to have; skip if minimising surface' },
];

const CopyBtn = ({ text }) => (
  <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
    onClick={() => { navigator.clipboard.writeText(text); toast.success('Copied'); }}>
    <Copy className="w-3 h-3" />
  </Button>
);

const Code = ({ children }) => (
  <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-xs">{children}</code>
);

const StepBadge = ({ n }) => (
  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0A7EA4] text-white text-xs font-bold">{n}</span>
);

const ZoomSetupGuideModal = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState('overview');
  // Prefill the platform origin for domain allowlisting
  const platformOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="zoom-setup-guide-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Video className="w-6 h-6 text-[#FF6B6B]" />
            Zoom Setup Guide
          </DialogTitle>
          <DialogDescription>
            Two Marketplace apps, ~10 minutes total. Everything stays in Development mode &mdash; no publishing needed.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="overview" data-testid="zoom-guide-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="s2s" data-testid="zoom-guide-tab-s2s"><Server className="w-4 h-4 mr-1" />S2S OAuth</TabsTrigger>
            <TabsTrigger value="sdk" data-testid="zoom-guide-tab-sdk"><Video className="w-4 h-4 mr-1" />Meeting SDK</TabsTrigger>
            <TabsTrigger value="map" data-testid="zoom-guide-tab-map"><MapPin className="w-4 h-4 mr-1" />Field Map</TabsTrigger>
            <TabsTrigger value="faq" data-testid="zoom-guide-tab-faq"><HelpCircle className="w-4 h-4 mr-1" />FAQ</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
              <p className="font-semibold mb-1 flex items-center gap-1.5"><Info className="w-4 h-4" /> Why two apps?</p>
              <p>Zoom splits &ldquo;scheduling meetings on your behalf&rdquo; and &ldquo;embedding meeting UI in your website&rdquo; into two independent products. You need one of each. Both stay <strong>internal / unpublished</strong>.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-5 h-5 text-[#0A7EA4]" />
                  <h4 className="font-semibold">App 1: Server-to-Server OAuth</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Backend calls Zoom REST API to create/update/cancel meetings.</p>
                <ul className="text-sm space-y-1.5">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Provides <Code>Account ID</Code>, <Code>Client ID</Code>, <Code>Client Secret</Code></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Requires ~10 scopes (listed under S2S tab)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Never needs publishing &mdash; internal by design</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Video className="w-5 h-5 text-[#FF6B6B]" />
                  <h4 className="font-semibold">App 2: Meeting SDK (General App)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Browser mounts Zoom&apos;s meeting UI inside our web page.</p>
                <ul className="text-sm space-y-1.5">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Provides <Code>SDK Key</Code> and <Code>SDK Secret</Code></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />No OAuth scopes &mdash; signature is JWT-signed</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Must add <Code>{platformOrigin}</Code> to Domain Allow List</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Development credentials are enough</li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">You do NOT need to publish anything on the Zoom Marketplace</p>
                <p>Publishing is required only if you want <em>other</em> Zoom accounts to install your app. For a private platform serving your own students, both apps live in <strong>Development</strong> mode indefinitely.</p>
              </div>
            </div>

            <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-[#0A7EA4] hover:underline text-sm font-medium">
              Open Zoom Marketplace <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </TabsContent>

          {/* S2S OAUTH */}
          <TabsContent value="s2s" className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><Server className="w-5 h-5" /> App 1 &mdash; Server-to-Server OAuth</h3>

            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <StepBadge n={1} />
                <div className="flex-1">
                  Go to <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noreferrer"
                    className="text-[#0A7EA4] hover:underline inline-flex items-center gap-1">Zoom Marketplace &rarr; Develop &rarr; Build App <ExternalLink className="w-3 h-3" /></a>
                </div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={2} />
                <div className="flex-1">Pick <strong>&ldquo;Server-to-Server OAuth&rdquo;</strong> as the app type. Give it a name (e.g. <Code>HelmTorq Scheduler</Code>).</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={3} />
                <div className="flex-1">On <strong>App Credentials</strong>, copy these three values &mdash; you&apos;ll paste them into HelmTorq later:
                  <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                    <li><Code>Account ID</Code> &rarr; <strong>Zoom Account ID</strong> field</li>
                    <li><Code>Client ID</Code> &rarr; <strong>S2S Client ID</strong> field</li>
                    <li><Code>Client Secret</Code> &rarr; <strong>S2S Client Secret</strong> field</li>
                  </ul>
                </div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={4} />
                <div className="flex-1">Fill <strong>Information</strong> (basic contact / company &mdash; doesn&apos;t matter for internal use).</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={5} />
                <div className="flex-1"><strong>Scopes</strong> tab &mdash; add each of these <em>exactly</em>:
                  <div className="mt-2 border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-2 py-1.5">Scope</th>
                          <th className="text-left px-2 py-1.5">Purpose</th>
                          <th className="w-16 px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {S2S_SCOPES.map(s => (
                          <tr key={s.scope} className="border-t">
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <Code>{s.scope}</Code>
                                <CopyBtn text={s.scope} />
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {s.purpose}
                              {s.note && <div className="text-xs text-gray-500 italic mt-0.5">{s.note}</div>}
                            </td>
                            <td className="px-2 py-1.5">
                              {s.required ? (
                                <Badge className="bg-red-100 text-red-700 border border-red-200 hover:bg-red-100">Required</Badge>
                              ) : (
                                <Badge variant="outline">Optional</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Zoom&apos;s scope UI groups these under <em>Meetings</em> and <em>Users</em>. Type each scope name into the search box or click the checkboxes labeled &ldquo;View, Update, Create, Delete a meeting&rdquo; (both &ldquo;user&rdquo; and &ldquo;admin&rdquo; variants).</p>
                </div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={6} />
                <div className="flex-1"><strong>Activation</strong> tab &rarr; click <strong>Activate your app</strong>. Done. No publishing.</div>
              </li>
            </ol>
          </TabsContent>

          {/* MEETING SDK */}
          <TabsContent value="sdk" className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><Video className="w-5 h-5" /> App 2 &mdash; Meeting SDK (General App)</h3>

            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <StepBadge n={1} />
                <div className="flex-1">Go back to <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noreferrer" className="text-[#0A7EA4] hover:underline inline-flex items-center gap-1">Build App <ExternalLink className="w-3 h-3" /></a> and pick <strong>&ldquo;General App&rdquo;</strong>.</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={2} />
                <div className="flex-1">Name it (e.g. <Code>HelmTorq Live Class Embed</Code>). Keep <strong>User-managed</strong> selected.</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={3} />
                <div className="flex-1">Under <strong>Basic Information &rarr; App Credentials</strong> you&apos;ll see two credential sections. The <strong>Client ID / Client Secret</strong> under <em>Development</em> is what you copy:
                  <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
                    <li><Code>Development Client ID</Code> &rarr; <strong>Meeting SDK Key</strong> field</li>
                    <li><Code>Development Client Secret</Code> &rarr; <strong>Meeting SDK Secret</strong> field</li>
                  </ul>
                </div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={4} />
                <div className="flex-1">Scroll down to <strong>App Type</strong> &mdash; make sure <strong>Meeting SDK</strong> is enabled (check the toggle labeled &ldquo;Meeting SDK / Zoom Client SDK&rdquo;).</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={5} />
                <div className="flex-1">
                  <strong>Embed &rarr; Meeting SDK</strong> tab &rarr; scroll to <strong>Domain Allow List</strong> and add:
                  <div className="mt-2 p-3 bg-gray-50 border rounded-md flex items-center gap-2 font-mono text-xs">
                    <span className="flex-1">{platformOrigin.replace(/^https?:\/\//, '')}</span>
                    <CopyBtn text={platformOrigin.replace(/^https?:\/\//, '')} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Enter <strong>only the hostname</strong> (no <Code>https://</Code>, no trailing slash). If you later host on a custom domain, add that too.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={6} />
                <div className="flex-1">Under <strong>Scopes</strong>, the SDK app doesn&apos;t need any OAuth scopes &mdash; authentication is via the JWT signature we mint from the backend. Skip.</div>
              </li>
              <li className="flex gap-3">
                <StepBadge n={7} />
                <div className="flex-1"><strong>Activation</strong> &rarr; click <strong>Activate your app</strong>. Done. No publishing.</div>
              </li>
            </ol>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-900 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">If join fails with &ldquo;Invalid signature&rdquo; or &ldquo;errorCode 200&rdquo;</p>
                <p className="mt-1">99% of the time it&apos;s the <strong>Domain Allow List</strong>. Double-check the domain matches exactly what shows in the browser URL bar, including staging/preview subdomains.</p>
              </div>
            </div>
          </TabsContent>

          {/* FIELD MAP */}
          <TabsContent value="map" className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-5 h-5" /> Where each credential goes</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2">Zoom Marketplace field</th>
                    <th className="text-left px-3 py-2">Source app</th>
                    <th className="text-left px-3 py-2">Paste into HelmTorq field</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-mono text-xs">Account ID</td>
                    <td className="px-3 py-2"><Badge variant="outline">S2S OAuth</Badge></td>
                    <td className="px-3 py-2">Zoom Account ID</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-xs">Client ID</td>
                    <td className="px-3 py-2"><Badge variant="outline">S2S OAuth</Badge></td>
                    <td className="px-3 py-2">S2S Client ID</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-xs">Client Secret</td>
                    <td className="px-3 py-2"><Badge variant="outline">S2S OAuth</Badge></td>
                    <td className="px-3 py-2">S2S Client Secret</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-xs">Client ID (Development)</td>
                    <td className="px-3 py-2"><Badge className="bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/30 hover:bg-[#FF6B6B]/10">Meeting SDK</Badge></td>
                    <td className="px-3 py-2">Meeting SDK Key</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-xs">Client Secret (Development)</td>
                    <td className="px-3 py-2"><Badge className="bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/30 hover:bg-[#FF6B6B]/10">Meeting SDK</Badge></td>
                    <td className="px-3 py-2">Meeting SDK Secret</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Zoom account owner email</td>
                    <td className="px-3 py-2"><Badge variant="secondary">Zoom Account</Badge></td>
                    <td className="px-3 py-2">Default Zoom User</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">The <strong>Default Zoom User</strong> is the email of the Zoom user under whom meetings will be scheduled. Typically the account owner &mdash; the email you used to sign up for the Zoom account.</p>
          </TabsContent>

          {/* FAQ */}
          <TabsContent value="faq" className="space-y-3 text-sm">
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">Do I need to publish the app on Zoom Marketplace?</p>
              <p className="text-gray-700">No. Both apps stay in Development mode forever. Publishing is only for apps you want other Zoom customers to install &mdash; not relevant here.</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">Development credentials vs Production credentials on the Meeting SDK app?</p>
              <p className="text-gray-700">Use <strong>Development</strong> credentials. Production credentials only unlock during formal Marketplace review &mdash; not needed for embedding your own scheduled meetings. Development is fully-featured for our use case.</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">Do I need a Paid Zoom plan?</p>
              <p className="text-gray-700">Yes &mdash; a Zoom <strong>Pro plan or higher</strong> is required to use Server-to-Server OAuth. Free/Basic plans don&apos;t expose the necessary APIs.</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">Can I use one Zoom app for multiple licenses in HelmTorq?</p>
              <p className="text-gray-700">Yes &mdash; but each Zoom account only allows meetings to be scheduled under users on that account. If you buy a second Zoom license (separate Zoom account), create a second pair of apps on that account and add another license row here.</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">What if I rotate the Client Secret?</p>
              <p className="text-gray-700">Open the Zoom license row here &rarr; click Edit &rarr; enter the new secret in the corresponding field (other secret fields left blank stay unchanged) &rarr; confirm with your admin password &rarr; hit <strong>Test</strong> to re-verify.</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="font-semibold mb-1">The SDK CDN version we use is 3.13.2 &mdash; does the Meeting SDK app need to match?</p>
              <p className="text-gray-700">No. The Meeting SDK app in Marketplace is version-agnostic &mdash; the same Client ID works with any Meeting SDK JS version. Bumps to the CDN version don&apos;t affect your app config.</p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ZoomSetupGuideModal;
